import type { InvestmentFlowPoint, InvestmentPortfolioEvolutionPoint } from '@/types';
import type { EChartsOption } from '@/components/charts/EChart';
import { formatBRLCompact } from '@/lib/charts/formatters';
import { denseAxisLabel } from '@/lib/charts/axis';
import { TOOLTIP_DIVIDER, TOOLTIP_TEXT_HEADING, TOOLTIP_TEXT_MUTED, TOOLTIP_TEXT_STRONG } from '@/lib/charts/theme';
import { hexWithAlpha } from '@/lib/colors';
import { themePalette } from '@/lib/theme/tokens';

type TooltipParam = {
  dataIndex?: number;
};

export function buildPortfolioEvolutionChartOption(
  chartData: InvestmentPortfolioEvolutionPoint[],
  // Tooltip amounts are exact money — the caller passes a privacy-aware
  // formatter (useFormatCurrency) so hide-values masks them too.
  formatCurrency: (value: number) => string,
): EChartsOption {
  if (chartData.length === 0) return {};
  const pal = themePalette();
  const labels = chartData.map((d) => d.label);
  const seriesData = buildPortfolioEvolutionSeries(chartData);
  const isTrendingUp = chartData[chartData.length - 1].balance >= chartData[0].balance;
  const trendColor = isTrendingUp ? pal.amountPositive : pal.amountNegative;

  const markAreas: Array<[{ xAxis: string; itemStyle?: { color: string } }, { xAxis: string }]> = [];
  chartData.forEach((d) => {
    if (d.netFlow !== 0) {
      // ~6% tint of the signed-amount tokens; hexWithAlpha guards against a role
      // token ever resolving to a non-hex value (raw `${hex}0F` concat would not).
      markAreas.push([
        { xAxis: d.label, itemStyle: { color: hexWithAlpha(d.netFlow > 0 ? pal.amountPositive : pal.amountNegative, 0.06) } },
        { xAxis: d.label },
      ]);
    }
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: pal.border, type: 'dashed' } },
      formatter: (raw: unknown) => {
        const params = (Array.isArray(raw) ? raw : [raw]) as TooltipParam[];
        if (!params.length) return '';

        const idx = params[0].dataIndex ?? 0;
        const point = chartData[idx];
        const sourceLabel = point.source === 'Observed' ? 'Observado' : 'Estimado';
        const netFlowColor = point.netFlow >= 0 ? pal.amountPositive : pal.amountNegative;

        let html = `<div style="min-width:190px;"><div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:6px;"><span style="font-size:11px;font-weight:600;color:${TOOLTIP_TEXT_HEADING};">${point.label}</span><span style="font-size:10px;color:${TOOLTIP_TEXT_MUTED};">${sourceLabel}</span></div>`;
        html += `<div style="display:flex;align-items:center;gap:8px;font-size:12px;color:${TOOLTIP_TEXT_MUTED};padding:1px 0;"><span style="width:8px;height:8px;background:${pal.amountPositive};border-radius:50%;"></span><span style="flex:1;">Patrimônio</span><span style="color:${TOOLTIP_TEXT_STRONG};font-variant-numeric:tabular-nums;">${formatCurrency(point.balance)}</span></div>`;
        html += `<div style="margin-top:6px;border-top:1px solid ${TOOLTIP_DIVIDER};padding-top:5px;display:flex;align-items:center;gap:8px;font-size:12px;color:${TOOLTIP_TEXT_MUTED};"><span style="width:8px;height:8px;background:${netFlowColor};border-radius:50%;"></span><span style="flex:1;">Movimentação líquida</span><span style="color:${TOOLTIP_TEXT_STRONG};font-variant-numeric:tabular-nums;">${point.netFlow >= 0 ? '+' : ''}${formatCurrency(point.netFlow)}</span></div>`;
        html += '</div>';
        return html;
      },
    },
    legend: {
      data: ['Patrimônio'],
      top: 0,
      right: 0,
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      textStyle: { fontWeight: 600 },
    },
    grid: { left: 8, right: 12, top: 40, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: false,
      // Long month histories overrun the axis width; rotate + thin once dense
      // (shared with InvestmentsFlowChart's axis) so labels stay legible.
      axisLabel: { fontWeight: 600, ...denseAxisLabel(labels.length) },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: (v: number) => formatBRLCompact(v) },
      splitLine: { lineStyle: { color: pal.border } },
    },
    series: [
      {
        name: 'Patrimônio',
        type: 'line',
        data: seriesData,
        smooth: true,
        connectNulls: false,
        symbol: 'circle',
        symbolSize: 7,
        showSymbol: true,
        lineStyle: { width: 3, color: trendColor },
        itemStyle: { color: trendColor, borderColor: pal.surface, borderWidth: 2 },
        markArea: markAreas.length > 0 ? { silent: true, data: markAreas } : undefined,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: hexWithAlpha(trendColor, 0.22) },
              { offset: 1, color: hexWithAlpha(trendColor, 0) },
            ],
          },
        },
        animationDelay: (idx: number) => idx * 40,
      },
    ],
  };
}

export function buildPortfolioEvolutionChartData(
  evolution: InvestmentPortfolioEvolutionPoint[] | undefined,
  trends: InvestmentFlowPoint[],
  currentBalance: number,
  currentOriginal: number,
  currentProfit: number,
): InvestmentPortfolioEvolutionPoint[] {
  if (evolution?.length) {
    return normalizeFinalPortfolioEvolutionPoint(evolution, currentBalance, currentOriginal, currentProfit);
  }
  if (!trends || trends.length === 0) return [];

  let balance = currentBalance;
  let investedCapital = currentOriginal;
  const monthlyReturnFactor = calculateMonthlyReturnFactor(currentBalance, currentOriginal, trends.length);
  const reversed = trends
    .slice()
    .reverse()
    .map((trend, index) => {
      const netFlow = trend.contributions - trend.withdrawals;
      const point: InvestmentPortfolioEvolutionPoint = {
        label: trend.label,
        year: trend.year,
        month: trend.month,
        contributions: trend.contributions,
        withdrawals: trend.withdrawals,
        netFlow,
        balance,
        investedCapital,
        profit: index === 0 ? currentProfit : balance - investedCapital,
        source: index === 0 ? 'Observed' : 'Estimated',
      };
      balance = roundMoney(Math.max(0, (balance / monthlyReturnFactor) - netFlow));
      investedCapital = roundMoney(Math.max(0, investedCapital - netFlow));
      return point;
    });

  return normalizeFinalPortfolioEvolutionPoint(reversed.reverse(), currentBalance, currentOriginal, currentProfit);
}

export function buildPortfolioEvolutionSeries(chartData: InvestmentPortfolioEvolutionPoint[]) {
  return chartData.map((d) => d.balance);
}

function normalizeFinalPortfolioEvolutionPoint(
  chartData: InvestmentPortfolioEvolutionPoint[],
  currentBalance: number,
  currentOriginal: number,
  currentProfit: number,
): InvestmentPortfolioEvolutionPoint[] {
  if (chartData.length === 0) return [];
  const lastIndex = chartData.length - 1;

  return chartData.map((point, index) => (
    index === lastIndex
      ? {
          ...point,
          balance: currentBalance,
          investedCapital: currentOriginal,
          profit: currentProfit,
          source: 'Observed',
        }
      : point
  ));
}

function calculateMonthlyReturnFactor(currentBalance: number, currentOriginal: number, pointCount: number) {
  if (currentBalance <= 0 || currentOriginal <= 0 || pointCount <= 1) return 1;

  const totalReturnRatio = currentBalance / currentOriginal;
  const intervals = Math.max(1, pointCount - 1);
  const factor = Math.pow(totalReturnRatio, 1 / intervals);

  return Number.isFinite(factor) && factor > 0 ? factor : 1;
}

function roundMoney(value: number) {
  return Math.round(value * 100) / 100;
}
